import { useCallback, useContext } from "react";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import {
  Button,
  Color,
  HorizontalLine,
  LoadingGroup,
  LoadingSkeleton,
  Panel,
  Tag,
} from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Spacing, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading, Semibold, Subheading, Subtitle, TextColor } from "capitalrx-components-2.0/lib/typography";
import { formatDollars } from "capitalrx-utilities/lib/formatters";

import { ContentPanel, PageWrapper } from "@/App/components";
import { useNavigateClientNewTab } from "@/App/hooks";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";
import { StackOrShelf } from "@/App/components/StackOrShelf";
import { NavigateButton } from "@/App/components/NavigateButton";
import { useQueryClient } from "@tanstack/react-query";
import { MemberEligibilityContext } from "@/App/context";

import { ClaimsDetailServicePanel } from "./ClaimsDetailServicePanel";
import { ClaimInfoStack } from "../Claims";
import "./ClaimsDetails.scss";
import { useGetMedicalClaimDetails } from "@/App/api/claims";

export const DentalClaimDetailsPage = () => {
  const { t: translate } = useTranslation();
  const { id: claimId } = useParams();
  const { data: medicalClaimData, isLoading: isLoadingMedicalClaimData } = useGetMedicalClaimDetails({ id: +claimId });
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const navigateNewTab = useNavigateClientNewTab();
  const queryClient = useQueryClient();
  const { cardColumns, stackColumns, spacing } = useResponsive({
    cardColumns: {
      desktop: 6,
      tablet: 6,
      mobile: 12,
    },
    stackColumns: {
      desktop: 6,
      tablet: 12,
      mobile: 12,
    },
    spacing: {
      desktop: "large",
      tablet: "large",
      mobile: "mini",
    },
  });
  const navigate = useNavigate();

  const formattedServiceDate = medicalClaimData?.minimum_service_date ? DateTime.fromISO(medicalClaimData.minimum_service_date, { zone: "utc" }).toFormat("DDD") : "";
  const formattedReceivedDate = medicalClaimData?.submission_date ? DateTime.fromISO(medicalClaimData.submission_date, { zone: "utc" }).toFormat("DDD") : "";
  const copayCoinsuranceAmount = medicalClaimData?.total_amount_of_coinsurance + medicalClaimData?.total_amount_of_copay;

  const onBackPressed = useCallback(() => {
    logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.backButton);
    queryClient.removeQueries({ queryKey: ["dentalClaimDetails"] });
    navigate("/claims-history?claims_type=dental");
  }, []);

  let statusTagElement = <></>;
  if (medicalClaimData?.status === "denied") {
    statusTagElement = <Tag type="error-high-contrast">{translate("CLAIMS.statusOptions.denied")}</Tag>;
  } else if (medicalClaimData?.status === "partially_paid") {
    statusTagElement = <Tag type="system-high-contrast">{translate("CLAIMS.statusOptions.partiallyApproved")}</Tag>;
  } else if (medicalClaimData?.status === "pended") {
    statusTagElement = <Tag type="warning-high-contrast">{translate("CLAIMS.statusOptions.inProgress")}</Tag>;
  } else if (medicalClaimData?.status === "paid") {
    statusTagElement = <Tag type="success-high-contrast">{translate("CLAIMS.statusOptions.approved")}</Tag>;
  }

  let billingAddress = medicalClaimData?.billing_provider_address_line_1;
  if (medicalClaimData?.billing_provider_address_line_2) {
    billingAddress += `, ${medicalClaimData?.billing_provider_address_line_2}`;
  }
  if (medicalClaimData?.billing_provider_city && medicalClaimData?.billing_provider_state && medicalClaimData?.billing_provider_zip) {
    billingAddress += `, ${medicalClaimData?.billing_provider_city}, ${medicalClaimData?.billing_provider_state} ${medicalClaimData?.billing_provider_zip}`;
  }

  let totalBilled = 0;
  let totalAllowedByPlan = 0;
  let totalPlanPaid = 0;
  let totalPatientPay = 0;

  if (medicalClaimData?.status !== "pended" && medicalClaimData?.service_line_items.length > 0) {
    for (const lineItem of medicalClaimData.service_line_items) {
      totalBilled = totalBilled + (lineItem.line_item_charge_amount || 0);
      totalAllowedByPlan = totalAllowedByPlan + (lineItem.repriced_allowed_amount || 0);
      totalPlanPaid = totalPlanPaid + (lineItem.plan_pay_amount || 0);
      totalPatientPay = totalPatientPay + (lineItem.patient_pay_amount || 0);
    }
  }

  const handlePrintButtonClick = () => {
    const searchParams = new URLSearchParams();
    searchParams.append("claimId", claimId);

    if (isViewingDataholder) {
      searchParams.append("member", memberEligibility.member.id.toString());
    }
    if (medicalClaimData?.person_code) {
      searchParams.append("personCode", medicalClaimData?.person_code);
    }

    logEvent(ANALYTIC_EVENTS.CLAIM_DETAILS.printMedicalClaimDetails);
    navigateNewTab("/print-medical-claim-details", searchParams);
  };

  let billingProvider = "";
  if (medicalClaimData?.billing_provider_first_name) {
    billingProvider = medicalClaimData?.billing_provider_first_name + " " + medicalClaimData?.billing_provider_last_name_or_organizational_name;
  } else {
    billingProvider = medicalClaimData?.billing_provider_last_name_or_organizational_name;
  }

  let renderingProvider = "";
  if (medicalClaimData?.rendering_provider_first_name) {
    renderingProvider = medicalClaimData?.rendering_provider_first_name + " " + medicalClaimData?.rendering_provider_last_name_or_organizational_name;
  } else {
    renderingProvider = medicalClaimData?.rendering_provider_last_name_or_organizational_name;
  }

  const directMemberReimbursementIndicator = (medicalClaimData?.direct_member_reimbursement_indicator ?? "0") !== "0" ? (
    <Body>{translate("CLAIMS.claimsDetailsPage.reimbursementClaim")}</Body>
  ) : null;

  return (
    <PageWrapper>
      <ContentPanel
        title={
          <StackOrShelf alignment="top" spacing="mini">
            <Stack spacing="mini">
              <Shelf spacing="mini">
                <NavigateButton onClick={onBackPressed}>{translate("CLAIMS.claimsDetailsPage.back")}</NavigateButton>
                <Shelf.Spacer />
              </Shelf>
              <LoadingGroup loading={isLoadingMedicalClaimData}>
                <LoadingGroup.Placeholder
                  fallback={
                    <>
                      <LoadingSkeleton style={{ height: "30px", width: "250px", maxWidth: "100%" }} />
                      <LoadingSkeleton style={{ height: "20px", width: "150px" }} />
                      <LoadingSkeleton style={{ height: "20px", width: "250px", maxWidth: "100%" }} />
                    </>
                  }
                >
                  <Shelf>
                    <Subtitle testId={`claim-title`}>
                      {billingProvider}
                    </Subtitle>
                    <Shelf.Spacer />
                  </Shelf>
                  <StackOrShelf spacing={spacing as Spacing} alignment="left">
                    <Body>
                      {medicalClaimData?.network === "in_network_tier_1" && translate("CLAIMS.networkOptions.inNetwork")}
                      {medicalClaimData?.network === "out_of_network" && translate("CLAIMS.networkOptions.outOfNetwork")}
                    </Body>
                    {directMemberReimbursementIndicator}
                    <Shelf.Spacer />
                  </StackOrShelf>
                  <StackOrShelf spacing={spacing as Spacing} alignment="left">
                    <Shelf spacing="mini">
                      <Body><Semibold><TextColor appearance={Color.DarkGrey[400]}>ID:</TextColor></Semibold></Body>
                      <Body>{String(claimId).padStart(3, "0")}</Body>
                    </Shelf>
                    <Shelf spacing="mini">
                      <Body><Semibold><TextColor appearance={Color.DarkGrey[400]}>{translate("CLAIMS.claimsPage.serviceDate")}:</TextColor></Semibold></Body>
                      <Body>{formattedServiceDate}</Body>
                    </Shelf>
                    <Shelf spacing="mini">
                      <Body><Semibold><TextColor appearance={Color.DarkGrey[400]}>{translate("CLAIMS.claimListItem.receivedDate")}:</TextColor></Semibold></Body>
                      <Body>{formattedReceivedDate}</Body>
                    </Shelf>
                    <Shelf spacing="mini">
                      <Body><Semibold><TextColor appearance={Color.DarkGrey[400]}>{translate("CLAIMS.claimListItem.status")}:</TextColor></Semibold></Body>
                      <Body>{statusTagElement}</Body>
                    </Shelf>
                  </StackOrShelf>
                </LoadingGroup.Placeholder>
              </LoadingGroup>
            </Stack>
            <Shelf.Spacer />
            <Button
              testId="generate-claim-details-pdf-button"
              appearance="text"
              icon="print"
              onClick={handlePrintButtonClick}
            >
              {translate("CLAIMS.claimsDetailsPage.printPage")}
            </Button>
          </StackOrShelf>
        }
      >
        <Grid>
          <Grid.Span columns={cardColumns}>
            <Stack spacing="large">
              <LoadingGroup loading={isLoadingMedicalClaimData}>
                <LoadingGroup.Placeholder
                  fallback={
                    <>
                      <Stack>
                        <LoadingSkeleton style={{ height: "24px", width: "150px" }} />
                        <Panel appearance="primary" className="member-portal-panel">
                          <Stack>
                            <LoadingSkeleton style={{ height: "20px", width: "100%" }} />
                            <LoadingSkeleton style={{ height: "20px", width: "75%" }} />
                            <HorizontalLine />
                            <LoadingSkeleton style={{ height: "20px", width: "100%" }} />
                            <LoadingSkeleton style={{ height: "20px", width: "75%" }} />
                            <HorizontalLine />
                            <LoadingSkeleton style={{ height: "24px", width: "100%" }} />
                          </Stack>
                        </Panel>
                      </Stack>
                      <Stack>
                        <LoadingSkeleton style={{ height: "24px", width: "150px" }} />
                        <LoadingSkeleton style={{ height: "150px", width: "100%" }} />
                        <LoadingSkeleton style={{ height: "150px", width: "100%" }} />
                      </Stack>
                    </>
                  }
                >
                  {
                    medicalClaimData?.status !== "pended" && (
                      <Stack>
                        <Heading>{translate("CLAIMS.claimsDetailsPage.paymentSummary")}</Heading>
                        <Panel appearance="primary" className="member-portal-panel">
                          <Stack>
                            <Shelf><Body>{translate("CLAIMS.claimsDetailsPage.totalAmountBilled")}</Body><Shelf.Spacer />{formatDollars(totalBilled)}</Shelf>
                            {medicalClaimData?.status !== "denied" &&
                              <Shelf>
                                <Body>{translate("CLAIMS.claimsDetailsPage.totalAllowedByPlan")}</Body>
                                <Shelf.Spacer />
                                {formatDollars(totalAllowedByPlan)}
                              </Shelf>
                            }
                            <HorizontalLine />
                            <Shelf><Body>{translate("CLAIMS.claimsDetailsPage.totalPlanPays")}</Body><Shelf.Spacer />-{formatDollars(totalPlanPaid)}</Shelf>
                            {medicalClaimData?.status === "paid" && (
                              <Shelf>
                                <Body>{translate("CLAIMS.claimsDetailsPage.deductible")}</Body>
                                <Shelf.Spacer />
                                {formatDollars(medicalClaimData?.total_amount_applied_to_deductible || "0")}
                              </Shelf>
                            )}
                            {medicalClaimData?.status !== "denied" && <Shelf><Body>{translate("CLAIMS.claimsDetailsPage.copayCoinsurance")}</Body><Shelf.Spacer />{formatDollars(copayCoinsuranceAmount || "0")}</Shelf>}
                            <HorizontalLine />
                            <Shelf>
                              <Subheading>{translate("CLAIMS.claimsDetailsPage.patientResponsibility")}</Subheading>
                              <Shelf.Spacer />
                              <Subheading>{formatDollars(totalPatientPay)}</Subheading>
                            </Shelf>
                          </Stack>
                        </Panel>
                      </Stack>
                    )
                  }
                  {
                    medicalClaimData?.service_line_items?.length > 0 && (
                      <Stack>
                        <Heading>{translate("CLAIMS.claimsDetailsPage.services")}</Heading>
                        <Stack>
                          {
                            medicalClaimData?.service_line_items.map((service, index) => <ClaimsDetailServicePanel key={index} service={service} /> )
                          }
                        </Stack>
                      </Stack>
                    )
                  }
                </LoadingGroup.Placeholder>
              </LoadingGroup>
            </Stack>
          </Grid.Span>
          <Grid.Span columns={cardColumns}>
            <LoadingGroup loading={isLoadingMedicalClaimData}>
              <LoadingGroup.Placeholder
                fallback={
                  <>
                    <Stack>
                      <LoadingSkeleton style={{ height: "24px", width: "150px" }} />
                      <Panel appearance="primary" className="member-portal-panel">
                        <Stack>
                          <Stack spacing="mini" alignment="left">
                            <LoadingSkeleton style={{ height: "20px", width: "100px" }} />
                            <LoadingSkeleton style={{ height: "20px", width: "175px" }} />
                          </Stack>
                          <Stack spacing="mini" alignment="left">
                            <LoadingSkeleton style={{ height: "20px", width: "100px" }} />
                            <LoadingSkeleton style={{ height: "20px", width: "175px" }} />
                          </Stack>
                          <Stack spacing="mini" alignment="left">
                            <LoadingSkeleton style={{ height: "20px", width: "100px" }} />
                            <LoadingSkeleton style={{ height: "20px", width: "175px" }} />
                          </Stack>
                        </Stack>
                      </Panel>
                    </Stack>
                  </>
                }
              >
                <Stack>
                  <Heading>{translate("CLAIMS.claimsDetailsPage.providerDetails")}</Heading>
                  <Panel appearance="primary" className="member-portal-panel">
                    <Grid>
                      <Grid.Span columns={stackColumns}>
                        <ClaimInfoStack
                          title={translate("CLAIMS.claimsDetailsPage.billingProvider")}
                          body={billingProvider}
                        />
                      </Grid.Span>
                      <Grid.Span columns={stackColumns}>
                        <ClaimInfoStack
                          title={translate("CLAIMS.claimsDetailsPage.renderingProvider")}
                          body={renderingProvider}
                        />
                      </Grid.Span>
                      <Grid.Span columns={stackColumns}>
                        <ClaimInfoStack
                          title={translate("CLAIMS.claimsDetailsPage.address")}
                          body={billingAddress}
                        />
                      </Grid.Span>
                    </Grid>
                  </Panel>
                </Stack>
              </LoadingGroup.Placeholder>
            </LoadingGroup>
          </Grid.Span>
        </Grid>
      </ContentPanel>
    </PageWrapper>
  );
};
